/* eslint-disable */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Button } from '@/Components/ui/button';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';

const UniversalSelectionTable = ({
    dataList = [],
    columns = [],
    selectionType = 'single',
    legend,
    heading,
    actionButtons = [],
    keyExtractor = (item) => item?.id,
    emptyMessage = "No items found.",
    onSelectionChange,
    showSubRow = false,
    renderSubRow = () => null
}) => {
    const [selectedItems, setSelectedItems] = useState(selectionType === 'single' ? null : []);
    const [searchTerm, setSearchTerm] = useState('');
    const tableRef = useRef(null);

    useEffect(() => {
        if (!tableRef.current) return;

        // Use a short timeout to ensure the DOM has painted all TRs first
        const timeoutId = setTimeout(() => {
            const trs = tableRef.current.querySelectorAll('tbody tr');
            const lowerSearch = searchTerm.toLowerCase();

            trs.forEach(tr => {
                if (!searchTerm) {
                    tr.style.display = '';
                    return;
                }

                // Read the actual plain text rendered in the entire row
                const textContent = tr.textContent || tr.innerText || '';

                if (textContent.toLowerCase().includes(lowerSearch)) {
                    tr.style.display = '';
                } else {
                    tr.style.display = 'none';
                }
            });
        }, 50);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, dataList]);

    const filteredData = dataList;

    const handleRowClick = (item) => {
        if (selectionType === 'single') {
            setSelectedItems(item);
            if (onSelectionChange) onSelectionChange(item);
        } else {
            const isSelected = selectedItems.find(i => keyExtractor(i) === keyExtractor(item));
            let newSelection;
            if (isSelected) {
                newSelection = selectedItems.filter(i => keyExtractor(i) !== keyExtractor(item));
            } else {
                newSelection = [...selectedItems, item];
            }
            setSelectedItems(newSelection);
            if (onSelectionChange) onSelectionChange(newSelection);
        }
    };

    const isItemSelected = (item) => {
        if (selectionType === 'single') {
            return selectedItems && keyExtractor(selectedItems) === keyExtractor(item);
        }
        return selectedItems.find(i => keyExtractor(i) === keyExtractor(item));
    };

    const toggleSelectAll = () => {
        if (selectedItems.length === dataList.length) {
            setSelectedItems([]);
            if (onSelectionChange) onSelectionChange([]);
        } else {
            setSelectedItems([...dataList]);
            if (onSelectionChange) onSelectionChange([...dataList]);
        }
    };

    const tableColumns = useMemo(() => {
        return [
            { key: 'selectRow', title: 'Select', width: 80, fixed: true },
            ...columns.map((col, i) => ({
                key: col.key || `col_${i}`,
                title: col.label,
                width: col.width || 150,
            }))
        ];
    }, [columns]);

    const customRowData = useMemo(() => {
        const rowData = {
            selectRow: (item) => {
                return (
                    <div className="flex justify-center w-full min-h-[30px] items-center" onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(item);
                    }}>
                        <input
                            type={selectionType === 'single' ? 'radio' : 'checkbox'}
                            checked={!!isItemSelected(item)}
                            onChange={() => handleRowClick(item)}
                            onClick={(e) => e.stopPropagation()}
                            className="cursor-pointer h-4 w-4"
                        />
                    </div>
                );
            }
        };
        columns.forEach((col, i) => {
            const key = col.key || `col_${i}`;
            if (col.render) {
                rowData[key] = (item) => col.render(item);
            } else {
                rowData[key] = (item) => {
                    if (!col.key) return '-';
                    const keys = col.key.split('.');
                    let val = item;
                    for (let k of keys) {
                        if (val === undefined || val === null) return '-';
                        val = val[k];
                    }
                    return val ?? '-';
                }
            }
        });
        return rowData;
    }, [columns, isItemSelected, selectionType]);

    const customCol = (col) => {
        if (col.key === 'selectRow') {
            return (
                <div className="flex justify-center items-center w-full">
                    {selectionType === 'multiple' ? (
                        <input
                            type="checkbox"
                            checked={selectedItems.length === dataList.length && dataList.length > 0}
                            onChange={toggleSelectAll}
                            className="h-4 w-4"
                        />
                    ) : (
                        <span className="text-sm font-semibold text-gray-700">Select</span>
                    )}
                </div>
            );
        }
        return <span className="font-semibold text-gray-700">{col.title}</span>;
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center mb-2">
                <div>
                    {heading && <h3 className="text-lg font-semibold text-gray-800">{heading}</h3>}
                    {legend && <p className="text-sm text-gray-500 mt-1">{legend}</p>}
                </div>
                <div>
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                </div>
            </div>

            <div ref={tableRef} className="border border-gray-200 rounded-md overflow-hidden" style={{ minHeight: '30vh', height: '50vh' }}>
                <VariantTable
                    data={filteredData?.map(item => ({ ...item, onClick: () => handleRowClick(item) }))}
                    columns={tableColumns}
                    customRowData={customRowData}
                    customCol={customCol}
                    customInfoLine={emptyMessage}
                    showSubRow={showSubRow}
                    renderSubRow={renderSubRow}
                />
            </div>

            {actionButtons && actionButtons.length > 0 && (
                <div className="flex justify-end gap-2 mt-2 pt-4 border-t">
                    {actionButtons.map((btn, idx) => (
                        <Button
                            key={idx}
                            variant={btn.variant || "default"}
                            disabled={typeof btn.disabled === 'function' ? btn.disabled(selectedItems) : btn.disabled}
                            onClick={() => btn.onClick(selectedItems)}
                            className={`font-semibold px-4 py-2 flex items-center justify-center ${btn.className || ''}`}
                            style={btn.style}
                        >
                            {btn.label}
                        </Button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default UniversalSelectionTable;
